import React, { useState, useEffect } from 'react';
import api from '../../utils/api';
import { useNavigate } from 'react-router-dom';
import { useToast } from '../../context/ToastContext';

export default function EmployeeAdd() {
  const navigate = useNavigate();
  const { showToast } = useToast();

  const [submitting, setSubmitting] = useState(false);
  const [stillWorking, setStillWorking] = useState(false);

  const [form, setForm] = useState({
    nip: '',
    noNik: '',
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    dateStartWork: '',
    dateEndWork: '',
    linkedIn: '',
    fileCv: '',
    systemRole: 'STAFF',
  });

  const handleChange = (e) => {
    setForm({
      ...form,
      [e.target.name]: e.target.value,
    });
  };

  const handleStillWorking = () => {
    setStillWorking(!stillWorking);

    setForm({
      ...form,
      dateEndWork: stillWorking ? '' : null, // jika dicentang → null
    });
  };

  // ---------------------------
  // VALIDASI
  // ---------------------------
  const validate = () => {
    if (
      !form.noNik ||
      !form.firstName ||
      !form.lastName ||
      !form.email ||
      !form.password
    ) {
      showToast('Field bertanda * wajib diisi.', 'danger');
      return false;
    }

    if (form.fileCv && !form.fileCv.startsWith('https://drive.google.com/')) {
      showToast('CV harus berupa link Google Drive yang valid.', 'danger');
      return false;
    }

    return true;
  };

  // ---------------------------
  // SUBMIT FORM
  // ---------------------------
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    try {
      setSubmitting(true);

      const payload = {
        nip: form.nip || '',
        noNik: form.noNik,
        firstName: form.firstName,
        lastName: form.lastName,
        email: form.email,
        password: form.password,
        dateStartWork: form.dateStartWork || null,
        dateEndWork: stillWorking ? null : form.dateEndWork || null,
        linkedIn: form.linkedIn || '',
        fileCv: form.fileCv || '',
        systemRole: form.systemRole,
      };

      await api.addEmployee(payload);

      navigate('/employee', {
        state: {
          toastMessage: 'Employee berhasil ditambahkan!',
          toastType: 'success',
        },
      });
    } catch (err) {
      console.error(err);
      showToast('Terjadi kesalahan saat menyimpan data.', 'danger');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="container py-4" style={{ maxWidth: '720px' }}>
      {/* Back Button */}
      <div className="mb-3">
        <button
          type="button"
          className="btn btn-white btn-sm rounded-circle shadow-sm border bg-white text-dark d-inline-flex align-items-center justify-content-center"
          style={{ width: '38px', height: '38px' }}
          onClick={() => navigate(-1)}
        >
          <i className="bi bi-arrow-left fs-5"></i>
        </button>
      </div>

      {/* HEADER */}
      <div className="mb-3">
        <h3 className="fw-bold">Tambah Karyawan Baru</h3>
        <p className="text-muted">
          Field bertanda <span className="text-danger">*</span> wajib diisi.
          Pastikan data yang Anda masukkan benar.
        </p>
      </div>

      <div className="card border-0 shadow-sm rounded-4">
        <div className="card-body p-4">
          <form onSubmit={handleSubmit} className="row g-3">
            {/* NIP */}
            <div className="col-md-6">
              <label className="form-label fw-semibold">NIP</label>
              <input
                type="text"
                className="form-control rounded-3"
                name="nip"
                value={form.nip}
                onChange={handleChange}
                placeholder="Opsional"
              />
            </div>

            {/* NIK */}
            <div className="col-md-6">
              <label className="form-label fw-semibold">
                No NIK <span className="text-danger">*</span>
              </label>
              <input
                type="text"
                className="form-control rounded-3"
                name="noNik"
                value={form.noNik}
                onChange={handleChange}
                required
                placeholder="Wajib diisi"
              />
            </div>

            {/* First name */}
            <div className="col-md-6">
              <label className="form-label fw-semibold">
                Nama Depan <span className="text-danger">*</span>
              </label>
              <input
                type="text"
                className="form-control rounded-3"
                name="firstName"
                value={form.firstName}
                onChange={handleChange}
                required
              />
            </div>

            {/* Last name */}
            <div className="col-md-6">
              <label className="form-label fw-semibold">
                Nama Belakang <span className="text-danger">*</span>
              </label>
              <input
                type="text"
                className="form-control rounded-3"
                name="lastName"
                value={form.lastName}
                onChange={handleChange}
                required
              />
            </div>

            {/* Email */}
            <div className="col-md-6">
              <label className="form-label fw-semibold">
                Email <span className="text-danger">*</span>
              </label>
              <input
                type="email"
                className="form-control rounded-3"
                name="email"
                value={form.email}
                onChange={handleChange}
                required
              />
            </div>

            {/* Password */}
            <div className="col-md-6">
              <label className="form-label fw-semibold">
                Password <span className="text-danger">*</span>
              </label>
              <input
                type="password"
                className="form-control rounded-3"
                name="password"
                value={form.password}
                onChange={handleChange}
                required
                placeholder="Minimal 6 karakter"
              />
            </div>

            {/* System Role */}
            <div className="col-12">
              <label className="form-label fw-semibold">
                Peran Sistem (System Role) <span className="text-danger">*</span>
              </label>
              <select
                className="form-select rounded-3"
                name="systemRole"
                value={form.systemRole}
                onChange={handleChange}
                required
              >
                <option value="STAFF">Staff (Default)</option>
                <option value="MANAGER">Manager</option>
              </select>
            </div>

            {/* Status Kerja Switch */}
            <div className="col-12">
              <div className="card bg-light border-0 rounded-4 p-3 mb-2">
                <div className="d-flex align-items-center justify-content-between">
                  <div>
                    <h6 className="fw-bold mb-1">
                      <i className="bi bi-briefcase-fill text-primary me-2"></i>Status Karyawan Aktif
                    </h6>
                    <p className="text-muted small mb-0">Aktifkan switch ini jika karyawan saat ini masih bekerja aktif di sini.</p>
                  </div>
                  <div className="form-check form-switch fs-4">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      role="switch"
                      checked={stillWorking}
                      onChange={handleStillWorking}
                      id="stillWorking"
                      style={{ cursor: 'pointer' }}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Start Work */}
            <div className="col-md-6">
              <label className="form-label fw-semibold">
                Tanggal Mulai Kerja
              </label>
              <input
                type="date"
                className="form-control rounded-3"
                name="dateStartWork"
                value={form.dateStartWork}
                onChange={handleChange}
              />
            </div>

            {/* End Work */}
            <div className="col-md-6">
              <label className="form-label fw-semibold">
                Tanggal Akhir Kerja
              </label>
              <input
                type="date"
                className="form-control rounded-3"
                name="dateEndWork"
                disabled={stillWorking}
                value={stillWorking ? '' : (form.dateEndWork || '')}
                onChange={handleChange}
                placeholder="Pilih tanggal jika sudah resign"
              />
            </div>

            {/* LinkedIn */}
            <div className="col-12">
              <label className="form-label fw-semibold">LinkedIn URL</label>
              <input
                type="text"
                className="form-control rounded-3"
                name="linkedIn"
                value={form.linkedIn}
                onChange={handleChange}
                placeholder="Opsional"
              />
            </div>

            {/* CV Google Drive */}
            <div className="col-12">
              <label className="form-label fw-semibold">
                CV (Google Drive)
              </label>
              <input
                type="text"
                className="form-control rounded-3"
                name="fileCv"
                value={form.fileCv}
                onChange={handleChange}
                placeholder="Masukkan link drive.google.com"
              />
              <small className="text-muted">
                Contoh: https://drive.google.com/file/d/xxxx/view
              </small>
            </div>

            {/* ACTION BUTTONS */}
            <div className="d-flex justify-content-end mt-3">
              <button
                type="button"
                className="btn btn-light rounded-pill me-2"
                onClick={() => navigate('/employee')}
                disabled={submitting}
              >
                Batal
              </button>

              <button
                type="submit"
                className="btn btn-primary rounded-pill px-4"
                disabled={submitting}
              >
                {submitting ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2"></span>
                    Menyimpan...
                  </>
                ) : (
                  <>
                    <i className="bi bi-save me-2"></i> Simpan
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
